import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ThemeState } from '@/types/store';

// Check if user's system prefers dark mode
const prefersDarkMode =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const initialState: ThemeState = {
  mode: prefersDarkMode ? 'dark' : 'light',
  colorScheme: 'default',
  systemPreference: true,
  primary: '#646cff',
  secondary: '#535bf2',
  accent: '#747bff',
  text: prefersDarkMode ? '#ffffff' : '#213547',
  background: prefersDarkMode ? '#242424' : '#ffffff',
  surface: prefersDarkMode ? '#2a2a2a' : '#f9f9f9',
  error: '#ff5555',
  warning: '#ffb86c',
  info: '#8be9fd',
  success: '#50fa7b',
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.mode = action.payload;
      state.systemPreference = false;

      // Apply theme to document
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(action.payload);
    },

    setColorScheme: (state, action: PayloadAction<'default' | 'blue' | 'green' | 'purple'>) => {
      state.colorScheme = action.payload;
    },

    toggleMode: state => {
      state.mode = state.mode === 'light' ? 'dark' : 'light';
      state.systemPreference = false;

      // Apply theme to document
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(state.mode);
    },

    useSystemPreference: state => {
      state.systemPreference = true;
      const prefersDark =
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

      state.mode = prefersDark ? 'dark' : 'light';

      // Apply theme to document
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(state.mode);
    },
  },
});

// Apply initial theme to document
if (typeof document !== 'undefined') {
  document.documentElement.classList.add(initialState.mode);
}

export const { setMode, setColorScheme, toggleMode, useSystemPreference } = themeSlice.actions;

export default themeSlice.reducer;
